<template>
  <div>
    <v-breadcrumbs
      :items="breadcrumbsItems"
      divider="/"
      large
    />

    <v-card
      class="mx-4"
      elevation="4"
      v-if="challenge"
    >
      <v-card-title>
        <v-chip
          v-if="!challenge.publish"
          color="error"
          class="mr-4"
        >
          非公開
        </v-chip>

        <span class="text-h5 font-weight-bold">{{ challenge.name }}</span>
        <span class="ma-2"></span>
        <span class="text-subtitle-1">{{ challenge.description }}</span>

        <v-spacer />

        <v-btn
          v-if="$store.state.user.uid == challenge.user"
          class="mr-6"
          :to="`/challenge/${challenge.name}/edit#detail`"
        >
          編集
        </v-btn>

        <v-btn
          v-if="$store.state.user.uid == challenge.user && !challenge.publish"
          color="success"
          class="mr-4"
          @click="publish(true)"
        >
          公開
        </v-btn>

        <v-btn
          v-if="$store.state.user.uid == challenge.user && challenge.publish"
          color="error"
          class="mr-4"
          @click="publish(false)"
        >
          非公開
        </v-btn>

        <v-menu
          open-on-hover
          bottom
          offset-y
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              color="primary"
              class="mr-2"
              v-bind="attrs"
              v-on="on"
            >
              ダウンロード
              <v-icon>mdi-menu-down</v-icon>
            </v-btn>
          </template>

          <v-list>
            <v-list-item
              @click="download"
            >
              <v-list-item-title>ファイルに保存</v-list-item-title>
            </v-list-item>
            <v-list-item
              @click="copy"
            >
              <v-list-item-title>ターミナルにコピー</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <v-btn
          icon
          :color="starColor"
          :disabled="!$store.state.user.authenticated"
          @click="toggleStar"
        >
          <v-icon>mdi-star</v-icon>
        </v-btn>
        <span class="mr-4">
          {{ starCount }}
        </span>

      </v-card-title>

      <div class="mx-2">
        <v-chip
          v-if="open"
          class="ma-1"
          color="yellow darken-4"
          outlined
        >
          <v-icon small class="mr-1">mdi-trophy</v-icon>
          開催中
        </v-chip>

        <v-chip
          v-for="tag in challenge.tags"
          :key="tag"
          :to="`/?tag=${tag}`"
          class="mx-1"
          color="primary"
          outlined
        >
          <v-icon small class="mr-1">mdi-tag</v-icon>
          {{ tag }}
        </v-chip>
      </div>

      <v-tabs>
        <v-tab to="#top">チャレンジ</v-tab>
        <v-tab to="#submit" v-if="$store.state.user.authenticated">提出</v-tab>
        <v-tab to="#list">提出一覧</v-tab>
        <v-tab to="#ranking">ランキング</v-tab>
  
        <v-tab-item value="top">
          <v-divider></v-divider>
  
          <v-container>
            <v-row>
              <v-col
                cols="12" md="8"
              >
                <div
                  v-if="public_files && public_files.readme"
                >
                  <Markdown :markdown-text="public_files.readme" />
                  <div class="md">
                    <h1>カーネルヘッダー</h1>
                    <HighlightedCode :code-text="public_files.kernel_header" />
                    <h1>テストベンチ</h1>
                    <HighlightedCode :code-text="public_files.public_tb" />
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="4">
                <v-simple-table>
                  <template v-slot:default>
                    <thead>
                    </thead>
                    <tbody>
                      <tr>
                        <th>提出期限</th>
                        <td>{{ challenge.expireAt.toDate().toISOString().substr(0, 10) }}</td>
                      </tr>
                    </tbody>
    
                    <thead>
                      <tr>
                        <th class="text-left" colspan="2">
                          合成制約
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <th>クロック周期</th>
                        <td>{{ challenge.regulation.clockPeriod }} ns</td>
                      </tr>
                    </tbody>

                    <thead>
                      <tr>
                        <th class="text-left" colspan="2">
                          採点条件
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <th>論理合成</th>
                        <td>{{ challenge.regulation.do_synth !== false ? "有効" : "無効" }}</td>
                      </tr>
                    </tbody>
    
                    <thead>
                      <tr>
                        <th class="text-left" colspan="2">
                          リソース制限
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="item in resource_constraints"
                        :key="item.key"
                      >
                        <th>{{ item.name }}</th>
                        <td>{{ resourceText(challenge.regulation[item.key]) }}</td>
                      </tr>
                    </tbody>
    
                    <thead>
                      <tr>
                        <th class="text-left" colspan="2">
                          実行時間制限
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="item in runtime_constraints"
                        :key="item.key"
                      >
                        <th>{{ item.name }}</th>
                        <td>{{ resourceText(challenge.regulation[item.key]) }} 分</td>
                      </tr>
                    </tbody>

                    <thead>
                      <tr>
                        <th class="text-left" colspan="2">
                          ビルド条件
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <th>コンパイルフラグ</th>
                        <td>{{ challenge.regulation.cxxflags }}</td>
                      </tr>
                      <tr>
                        <th>リンクフラグ</th>
                        <td>{{ challenge.regulation.ldflags }}</td>
                      </tr>
                      <tr>
                        <th>ツール</th>
                        <td>{{ challenge.regulation.tool_ver }}</td>
                      </tr>
                    </tbody>
    
                  </template>
                </v-simple-table>
              </v-col>
            </v-row>
          </v-container>
        </v-tab-item>

        <v-tab-item value="submit" v-if="$store.state.user.authenticated">
          <v-divider></v-divider>
          <v-container>
            <v-form
              ref="form"
              v-model="valid"
            >
              <h3 class="mb-3">カーネルソース (kernel.cpp)</h3>
              <v-textarea
                v-model="user_kernel_source"
                rows="20"
                outlined
                clearable
                :rules="codeRules"
                :counter="65000"
              ></v-textarea>

              <v-btn
                :disabled="!valid"
                color="success"
                class="mr-4"
                @click="submit"
              >
                提出
              </v-btn>
            </v-form>
          </v-container>
        </v-tab-item>

        <v-tab-item value="list">
          <v-divider></v-divider>
          <v-container>
          <div class="pa-2">
            <ResultList :query="query" :enableSort="true" />
          </div>
          </v-container>
        </v-tab-item>

        <v-tab-item value="ranking">
          <v-divider></v-divider>
          <v-container>
            <v-row>
              <v-col
                cols="12" md="12"
              >
                <v-list dense>
                  <v-list-item>
                    <v-list-item-icon><v-icon>mdi-chart-line</v-icon></v-list-item-icon>
                    <v-list-item-content>
                      記録更新チャート（Beta）
                    </v-list-item-content>
                  </v-list-item>
                </v-list>

                <RankingChart :history="challenge.ranking_history"/>
              </v-col>

              <v-col
                cols="12" md="6"
              >
                <v-list dense>
                  <v-list-item>
                    <v-list-item-icon><v-icon>mdi-podium</v-icon></v-list-item-icon>
                    <v-list-item-content>
                      期限内ランキング
                    </v-list-item-content>
                  </v-list-item>
                </v-list>

                <v-simple-table
                  v-if="ranking.length > 0"
                  class="mr-4"
                >
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left" colspan="2">
                          ランク
                        </th>
                        <th class="text-left">
                          ユーザー
                        </th>
                        <th class="text-left">
                          スコア
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <RankRow
                        v-for="item in ranking"
                        :key="item.user"
                        :item="item"
                      />
                    </tbody>
                  </template>
                </v-simple-table>
                <div
                  v-else
                  class="ma-4"
                >
                  データがありません
                </div>
              </v-col>

              <v-col
                cols="12" md="6"
              >
                <v-list dense>
                  <v-list-item>
                    <v-list-item-icon><v-icon>mdi-podium</v-icon></v-list-item-icon>
                    <v-list-item-content>
                      全期間ランキング
                    </v-list-item-content>
                  </v-list-item>
                </v-list>

                <v-simple-table
                  v-if="ranking_all.length > 0"
                  class="mr-4"
                >
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left" colspan="2">
                          ランク
                        </th>
                        <th class="text-left">
                          ユーザー
                        </th>
                        <th class="text-left">
                          スコア
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <RankRow
                        v-for="item in ranking_all"
                        :key="item.user"
                        :item="item"
                      />
                    </tbody>
                  </template>
                </v-simple-table>
                <div
                  v-else
                  class="ma-4"
                >
                  データがありません
                </div>
              </v-col>
            </v-row>
          </v-container>
        </v-tab-item>
      </v-tabs>
    </v-card>

    <v-snackbar
      color="success"
      v-model="saved"
      :timeout="4000"
    >
      {{ saved_message }}
    </v-snackbar>

    <v-snackbar
      color="error"
      v-model="failed"
      :timeout="6000"
    >
      エラー：{{ failed_message }}
    </v-snackbar>
  </div>
</template>

<style>
</style>

<script>
import jszip from 'jszip'
import pako from 'pako'
import FileSaver from 'file-saver'
import firebase from "@/firebase/firebase.js"
import {db} from "@/firebase/db.js"
import Markdown from '@/components/Markdown.vue'
import HighlightedCode from '@/components/HighlightedCode.vue'
import ResultList from '@/components/ResultList.vue'
import RankRow from '@/components/RankRow.vue'
import RankingChart from '@/components/RankingChart.vue'

function calcRanking(data, exclude) {
  const ranking = []
  for (const user in data) {
    if (!exclude.includes(user)) {
      ranking.push({ user: user, ...data[user] })
    }
  }
  ranking.sort((a, b) => a.score - b.score)
  let rank = 0
  let score = 0
  for (const r of ranking) {
    if (score < r.score) {
      ++rank
      score = r.score
    }
    r.rank = rank
  }
  return ranking //.slice(0, 10)
}

export default {
  components: {
    Markdown,
    HighlightedCode,
    ResultList,
    RankRow,
    RankingChart,
  },
  data: () => ({
    valid: false,
    user_kernel_source: "",
    challenge: null,
    user: null,
    challengeRef: null,
    public_files: null,
    results: [],
    query: null,
    saved: false,
    saved_message: "",
    failed: false,
    failed_message: "",
    unsubscribe: null,
    resource_constraints: [
      { key: 'max_ff',   result_key: 'ff',   name: 'FF' },
      { key: 'max_lut',  result_key: 'lut',  name: 'LUT' },
      { key: 'max_dsp',  result_key: 'dsp',  name: 'DSP' },
      { key: 'max_bram', result_key: 'bram', name: 'BRAM' },
      { key: 'max_uram', result_key: 'uram', name: 'URAM' },
    ],
    runtime_constraints: [
      { key: 'csimTimeout', name: 'CSIM' },
      { key: 'hlsTimeout', name: 'HLS' },
      { key: 'cosimTimeout', name: 'CoSIM' },
      { key: 'synTimeout', name: '論理合成' },
    ],
    result_table_headers: [
      { text: "ユーザー", value: 'user' },
      { text: "コード長", value: 'bytes' },
      { text: "結果", value: 'pass' },
      { text: "FF", value: 'ff' },
      { text: "LUT", value: 'lut' },
      { text: "DSP", value: 'dsp' },
      { text: "BRAM", value: 'bram' },
      { text: "URAM", value: 'uram' },
      { text: "周波数", value: 'clock_frequency' },
      { text: "タイム", value: 'sim_time' },
      { text: "提出日時", value: 'submittedAt' },
    ],
    test_items: [
      { name: 'csim_compile_error', text: "CSimコンパイルエラー" },
      { name: 'csim_runtime_error', text: "CSimランタイムエラー" },
      { name: 'csim_timeout',       text: "CSimタイムアウト" },
      { name: 'csim_mismatch',      text: "CSim不一致" },
      { name: 'csim_error',         text: "CSimエラー" },
      { name: 'csim_fail',          text: "CSimエラー" },
      { name: 'hls_timeout',        text: "HLSタイムアウト" },
      { name: 'hls_error',          text: "HLSエラー" },
      { name: 'hls_fail',           text: "HLSエラー" },
      { name: 'cosim_timeout',      text: "CoSimタイムアウト" },
      { name: 'cosim_mismatch',     text: "CoSim不一致" },
      { name: 'cosim_error',        text: "CoSimエラー" },
      { name: 'cosim_fail',         text: "CoSimエラー" },
    ],
    codeRules: [
      v => !!v || 'コードを入力してください',
      v => (v && v.length <= 65000) || '65000文字以下で入力してください',
    ],
  }),
  computed: {
    open: function () {
      const today = firebase.firestore.Timestamp.now()
      return this.challenge.expireAt >= today
    },
    starCount: function () {
      const stars = this.challenge.stars
      return stars ? stars.length : 0
    },
    starColor: function () {
      const uid = this.$store.state.user.uid
      const stars = this.challenge.stars
      return this.$store.state.user.authenticated && stars && stars.includes(uid) ?
        "yellow darken-3" : "grey"
    },
    breadcrumbsItems: function () {
      return this.user && this.challenge ? [
        { text: this.user.name, disabled: true, href: "" },
        { text: this.challenge.name, disabled: true, href: "" },
      ] : []
    },
    ranking: function() {
      return calcRanking(this.challenge.ranking || {}, [this.challenge.user])
    },
    ranking_all: function() {
      return calcRanking(this.challenge.ranking_all || {}, [this.challenge.user])
    },
  },
  watch: {
    public_files : function () {
      if (this.public_files) {
        this.user_kernel_source = this.public_files.kernel_source
      }
    },
  },
  methods: {
    resourceText(amount) {
      const v = parseInt(amount)
      if (isNaN(v))
        return amount
      else if (v < 0)
        return "無制限"
      else
        return amount.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,')
    },
    async submit() {
      try {
        const user = this.$store.state.user
        const uid = user.uid
        const userRef = db.collection("users").doc(uid)

        const filesRef = await userRef.collection("files").add({
          kernel_source: firebase.firestore.Blob.fromUint8Array(pako.deflate(this.user_kernel_source)),
          private: true,
          challengeId: this.challengeRef.id,
          challengeUserId: this.challenge.user,
        })

        const code = {
          user: uid,
          challengePath: this.challengeRef.path,
          files: filesRef.path,
          state: "submitted",
          pass: false,
          testing: true,
          submittedAt: firebase.firestore.FieldValue.serverTimestamp()
        }

        if (this.challenge.submitUsers === undefined || !this.challenge.submitUsers.includes(uid)) {
          await this.challengeRef.update({
            submitCount : firebase.firestore.FieldValue.increment(1),
            submitUsers : firebase.firestore.FieldValue.arrayUnion(uid),
          })
        }

        const codeRef = await userRef.collection("codes").add(code)

        this.$router.replace(`/user/${user.name}/code/${codeRef.id}#result`)
      } catch (e) {
        console.log(e)
        this.failed_message = e.message
        this.failed = true
      }
    },
    async publish(pub) {
      try {
        this.challengeRef.update({ publish: pub })
        this.saved_message = pub ? "チャレンジを公開に設定しました" : "チャレンジを非公開に設定しました"
        this.saved = true
      } catch (e) {
        this.failed_message = e.message
        this.failed = true
      }
    },
    async download() {
      // Get vhls-check
      const request = new XMLHttpRequest()
      request.open('GET', '/vhls-check', false)
      request.send(null)
      let checker = null
      if (request.status === 200) checker = request.responseText

      const zip = jszip()
      const folderName = this.challenge.name
      const folder = zip.folder(folderName)
      folder.file("README.md", this.public_files.readme)
      folder.file("kernel.hpp", this.public_files.kernel_header)
      folder.file("kernel.cpp", this.public_files.kernel_source)
      folder.file("tb.cpp", this.public_files.public_tb)
      folder.file("regulation.txt", this.challenge.regulation_text)
      if (checker) folder.file("vhls-check", checker)
      const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 9 } })
      FileSaver.saveAs(blob, `${folderName}.zip`)
    },
    async copy() {
      // Get vhls-check
      const request = new XMLHttpRequest()
      request.open('GET', '/vhls-check', false)
      request.send(null)
      let checker = null
      if (request.status === 200) checker = request.responseText

      const zip = jszip()
      const folderName = this.challenge.name
      const folder = zip.folder(folderName)
      folder.file("README.md", this.public_files.readme)
      folder.file("kernel.hpp", this.public_files.kernel_header)
      folder.file("kernel.cpp", this.public_files.kernel_source)
      folder.file("tb.cpp", this.public_files.public_tb)
      folder.file("regulation.txt", this.challenge.regulation_text)
      if (checker) folder.file("vhls-check", checker)
      const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 9 } })
      const reader = new FileReader()
      reader.onload = function () {
        let command = "cat << END | base64 -d | busybox unzip -\r"
        command += reader.result.split(',')[1]
        command += "\rEND"
        navigator.clipboard.writeText(command)
      }
      reader.readAsDataURL(blob)
    },
    async toggleStar() {
      if (this.$store.state.user.authenticated) {
        const uid = this.$store.state.user.uid
        if (this.challenge.stars === undefined || !this.challenge.stars.includes(uid)) {
          this.challengeRef.update({
            stars: firebase.firestore.FieldValue.arrayUnion(uid),
            starCount: firebase.firestore.FieldValue.increment(1),
          })
        } else {
          this.challengeRef.update({
            stars: firebase.firestore.FieldValue.arrayRemove(uid),
            starCount: firebase.firestore.FieldValue.increment(-1),
          })
        }
      }
    },
  },
  async created() {
    const cnameDoc = await db.collection("challengenames").doc(this.$route.params.challenge_name).get()
    if (cnameDoc.exists) {
      const userRef = db.collection("users").doc(cnameDoc.data().user)
      this.challengeRef = userRef.collection("challenges").doc(cnameDoc.data().challenge)
      this.$bind('user', userRef)
      this.$bind('challenge', this.challengeRef)

      this.query = db.collectionGroup("codes").where("challengePath", "==", this.challengeRef.path)

      const challenge = await this.challengeRef.get()
      this.$bind('public_files', db.doc(challenge.data().public_files))
    }
  },
};
</script>

